import { useCallback, useEffect, useRef, useState } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import SvgIcon from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import { DIALOG_CLOSE_BUTTON_SX } from "../../constants/dialogCloseButtonStyles";
import {
  ConceptsSelector,
  type ConceptsSelectorValue,
} from "../ImpactAssessmentChecklistForm";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { withoutBackdropClose } from "../../utils/withoutBackdropClose";
import { useGlobalConfig } from "../../hooks/useGlobalConfig";
import {
  buildMappingConceptsForSave,
  getProcessMappings,
  saveProcessMappings,
  type ProcessMappingRow,
} from "../../services/assessmentConfigService";
import { fetchValuesSearchConceptMetaByKey } from "../../services/regulationsService";
import noDataProcessMapping from "../../assets/no-data-process-mapping.png";

const PROCESS_MAPPING_EMPTY_MESSAGE =
  "No processes have been created yet. Please contact the support team to create processes first.";

/** Process row icon (custom asset). */
function ProcessMappingProcessIcon() {
  return (
    <SvgIcon
      viewBox="0 0 24 24"
      sx={{ width: 24, height: 24, flexShrink: 0, color: "primary.main" }}
    >
      <path
        d="M15 3C15 1.32 13.68 0 12 0C10.32 0 9 1.32 9 3C9 4.68 10.32 6 12 6C13.68 6 15 4.68 15 3ZM12 4.08C11.34 4.08 10.86 3.54 10.86 2.94C10.86 2.28 11.4 1.8 12 1.8C12.6 1.8 13.14 2.34 13.14 2.94C13.14 3.54 12.66 4.08 12 4.08ZM3 13.92C1.32 13.92 0 15.24 0 16.92C0 18.6 1.32 19.92 3 19.92C4.68 19.92 6 18.6 6 16.92C6 15.24 4.68 13.92 3 13.92ZM3 18C2.34 18 1.86 17.46 1.86 16.86C1.86 16.2 2.4 15.72 3 15.72C3.6 15.72 4.14 16.26 4.14 16.86C4.14 17.46 3.66 18 3 18ZM21 13.92C19.32 13.92 18 15.24 18 16.92C18 18.6 19.32 19.92 21 19.92C22.68 19.92 24 18.6 24 16.92C24 15.24 22.68 13.92 21 13.92ZM21.12 18C20.46 18 19.98 17.46 19.98 16.86C19.98 16.2 20.52 15.72 21.12 15.72C21.72 15.72 22.26 16.26 22.26 16.86C22.26 17.52 21.72 18 21.12 18ZM6.54 19.44L5.16 20.94C7.08 22.5 9.48 23.4 12 23.4C14.52 23.4 16.92 22.5 18.84 20.94L17.46 19.44C15.72 20.7 14.16 21.42 12 21.42C9.9 21.42 8.28 20.64 6.54 19.44ZM22.74 12.66C22.74 8.52 20.28 4.68 16.56 2.94L15.9 4.8C18.96 6.48 20.76 9.12 20.76 12.66H22.74ZM7.5 2.94L8.28 4.8C5.04 6.3 3.42 9.06 3.42 12.66H1.26C1.26 8.52 3.72 4.68 7.5 2.94ZM15.66 11.64H16.56C16.86 11.64 17.04 11.94 17.04 12.24V13.26C17.04 13.56 16.8 13.86 16.56 13.86H15.66C15.6 14.04 15.48 14.4 15.3 14.7L15.96 15.3C16.14 15.48 16.14 15.84 15.96 16.02L15.24 16.74C15.06 16.92 14.7 16.92 14.52 16.74L13.92 16.08C13.68 16.26 13.26 16.38 13.08 16.44V17.34C13.08 17.64 12.78 17.88 12.48 17.88H11.46C11.16 17.88 10.86 17.64 10.86 17.34V16.44C10.68 16.38 10.32 16.26 10.02 16.08L9.42 16.74C9.24 16.92 8.88 16.92 8.7 16.74L7.98 16.02C7.8 15.84 7.8 15.48 7.98 15.3L8.64 14.7C8.46 14.4 8.34 14.04 8.28 13.86H7.38C7.08 13.86 6.9 13.56 6.9 13.26V12.24C6.9 11.94 7.14 11.64 7.38 11.64H8.28C8.34 11.46 8.46 11.1 8.64 10.8L7.98 10.2C7.8 10.02 7.8 9.66 7.98 9.48L8.7 8.76C8.88 8.58 9.24 8.58 9.42 8.76L10.02 9.42C10.26 9.24 10.68 9.12 10.86 9.06V8.16C10.86 7.86 11.16 7.62 11.46 7.62H12.48C12.78 7.62 13.08 7.86 13.08 8.16V9.06C13.26 9.12 13.62 9.24 13.92 9.42L14.52 8.76C14.7 8.58 15.06 8.58 15.24 8.76L15.96 9.48C16.14 9.66 16.14 10.02 15.96 10.2L15.3 10.8C15.48 11.1 15.6 11.46 15.66 11.64ZM9.6 12.72C9.6 14.1 10.68 15.18 12.06 15.18C13.44 15.18 14.52 14.04 14.52 12.72C14.52 11.34 13.44 10.26 12.06 10.26C10.68 10.2 9.6 11.34 9.6 12.72Z"
        fill="currentColor"
      />
    </SvgIcon>
  );
}

function cloneConceptsState(
  source: Record<string, ConceptsSelectorValue>,
): Record<string, ConceptsSelectorValue> {
  const next: Record<string, ConceptsSelectorValue> = {};
  for (const [k, v] of Object.entries(source)) {
    next[k] = {
      selectedConceptIds: [...v.selectedConceptIds],
      selectedConceptsDetails: (v.selectedConceptsDetails ?? []).map((c) => ({
        ...c,
      })),
    };
  }
  return next;
}

export interface ProcessMappingSettingDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

export default function ProcessMappingSettingDialog({
  open,
  onClose,
}: ProcessMappingSettingDialogProps) {
  const { showMessage } = useSnackbar();
  const { config } = useGlobalConfig();
  const maxChecklistConcepts = config?.max_checklist_concepts ?? 25;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rows, setRows] = useState<ProcessMappingRow[]>([]);
  const [conceptsByProcess, setConceptsByProcess] = useState<
    Record<string, ConceptsSelectorValue>
  >({});
  const snapshotRef = useRef<Record<string, ConceptsSelectorValue>>({});
  const [expandedId, setExpandedId] = useState<string | false>(false);

  const applyRowsToState = useCallback((list: ProcessMappingRow[]) => {
    setRows(list);
    const initial: Record<string, ConceptsSelectorValue> = {};
    for (const r of list) {
      initial[r.id] = {
        selectedConceptIds: [...r.selectedConceptIds],
        selectedConceptsDetails: [...r.selectedConceptsDetails],
      };
    }
    setConceptsByProcess(initial);
    snapshotRef.current = cloneConceptsState(initial);
  }, []);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadError(null);
    setExpandedId(false);
    setLoading(true);
    void (async () => {
      try {
        const list = await getProcessMappings();
        if (cancelled) return;
        applyRowsToState(list);
      } catch (e) {
        if (cancelled) return;
        const msg =
          e instanceof Error ? e.message : "Failed to load process mappings.";
        setLoadError(msg);
        setRows([]);
        setConceptsByProcess({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, applyRowsToState]);

  const handleClose = useCallback(() => {
    setConceptsByProcess(cloneConceptsState(snapshotRef.current));
    onClose();
  }, [onClose]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const conceptMetaByKey = await fetchValuesSearchConceptMetaByKey();
      await saveProcessMappings(
        rows.map((r) => ({
          process_template_id: r.id,
          mapping_concepts: buildMappingConceptsForSave(
            conceptsByProcess[r.id]?.selectedConceptsDetails ?? [],
            conceptMetaByKey,
          ),
        })),
      );
      snapshotRef.current = cloneConceptsState(conceptsByProcess);
      showMessage("Process mappings saved.", "success");
      onClose();
    } catch (e) {
      const msg =
        e instanceof Error ? e.message : "Failed to save process mappings.";
      showMessage(msg, "error");
    } finally {
      setSaving(false);
    }
  }, [rows, conceptsByProcess, onClose, showMessage]);

  const updateProcessConcepts = useCallback(
    (processId: string, value: ConceptsSelectorValue) => {
      setConceptsByProcess((prev) => ({ ...prev, [processId]: value }));
    },
    [],
  );

  return (
    <Dialog
      open={open}
      onClose={withoutBackdropClose(handleClose)}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            maxHeight: "92vh",
            width: "100%",
            maxWidth: 1100,
            fontSize: "13px",
            lineHeight: 1.45,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          pl: 3,
          pr: 7,
          pb: 1,
          pt: 2,
          boxSizing: "border-box",
        }}
      >
        <Typography
          component="span"
          sx={{ fontWeight: 600, fontSize: "15px", lineHeight: 1.3 }}
        >
          Processes Mapping Setting
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          sx={{ ...DIALOG_CLOSE_BUTTON_SX, position: "absolute", right: 12, top: 10 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1, px: 1.5, pb: 0, fontSize: "13px" }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              gap: 1.5,
            }}
          >
            <CircularProgress size={28} />
            <Typography color="text.secondary" sx={{ fontSize: "13px" }}>
              Loading processes…
            </Typography>
          </Box>
        ) : loadError ? (
          <Typography color="error" sx={{ py: 2, fontSize: "13px" }}>
            {loadError}
          </Typography>
        ) : rows.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              minHeight: 320,
              py: 6,
              px: 3,
            }}
          >
            <Box
              component="img"
              src={noDataProcessMapping}
              alt=""
              sx={{ width: 160, height: "auto", mb: 2 }}
            />
            <Typography
              sx={{
                color: "#8c8c8c",
                fontSize: "14px",
                lineHeight: 1.5,
                maxWidth: 480,
              }}
            >
              {PROCESS_MAPPING_EMPTY_MESSAGE}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pb: 0 }}>
            {rows.map((row) => {
              const value = conceptsByProcess[row.id] ?? {
                selectedConceptIds: [],
                selectedConceptsDetails: [],
              };
              const n = value.selectedConceptIds.length;
              return (
                <Accordion
                  key={row.id}
                  disableGutters
                  elevation={0}
                  expanded={expandedId === row.id}
                  onChange={(_, exp) => setExpandedId(exp ? row.id : false)}
                  sx={{
                    border: "1px solid #E5E7EB",
                    borderRadius: "12px !important",
                    overflow: "hidden",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon sx={{ color: "#6B7280", fontSize: 20 }} />
                    }
                    sx={{
                      px: 3,
                      py: 1.25,
                      minHeight: 64,
                      boxSizing: "border-box",
                      "& .MuiAccordionSummary-content": {
                        alignItems: "center",
                        gap: 2,
                        my: 0,
                      },
                    }}
                  >
                    <ProcessMappingProcessIcon />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          color: "#9CA3AF",
                          display: "block",
                          mb: 0.25,
                          fontSize: "12px",
                          lineHeight: 1.35,
                        }}
                      >
                        ID: {row.id}
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: "#0F172A",
                          lineHeight: 1.3,
                          fontSize: "16px",
                        }}
                      >
                        {row.name}
                      </Typography>
                      <Typography
                        sx={{
                          color: "#0D0D0D",
                          mt: 0.5,
                          lineHeight: 1.4,
                          fontSize: "14px",
                        }}
                      >
                        {row.description || "Process description"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        flexShrink: 0,
                        alignSelf: "center",
                        height: "24px",
                        minHeight: "24px",
                        maxHeight: "24px",
                        boxSizing: "border-box",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "#F3F4F6",
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        px: 1.5,
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          color: "#374151",
                          fontWeight: 500,
                          fontSize: "13px",
                          lineHeight: 1,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {n}/{maxChecklistConcepts} Dimension selected
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      px: 0,
                      pb: 0,
                      pt: 0,
                      bgcolor: "#F9FAFB",
                      borderTop: "1px solid #E5E7EB",
                    }}
                  >
                    {expandedId === row.id ? (
                      <ConceptsSelector
                        variant="processMapping"
                        value={value}
                        onChange={(next) => updateProcessConcepts(row.id, next)}
                        maxTotalSelections={maxChecklistConcepts}
                        totalSelectionsCount={n}
                      />
                    ) : null}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          pt: 2.5,
          gap: 1.5,
          justifyContent: "flex-end",
          boxSizing: "border-box",
        }}
      >
        <Button
          variant="outlined"
          size="small"
          onClick={handleClose}
          disabled={saving}
          sx={{ fontSize: "13px", textTransform: "none", minWidth: 88 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={() => void handleSave()}
          disabled={loading || Boolean(loadError) || saving || rows.length === 0}
          sx={{
            fontSize: "13px",
            textTransform: "none",
            minWidth: 88,
            "&.Mui-disabled": {
              bgcolor: "#E5E7EB",
              color: "#9CA3AF",
            },
          }}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
